import React, {
  ComponentProps,
  memo,
  ReactNode,
  RefObject,
  useMemo,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import styled from '@emotion/styled'

import { useHandler } from '@surglogs-app/hooks'
import { Filter as FilterIcon } from '@surglogs-app/icons'

import { Box } from '../Box'
import { Button } from '../Button'
import { DropdownIcon } from '../DropdownIcon'
import { FlexBox } from '../FlexBox'

// default container for most of our layouts
export const DefaultFilterContentContainer = styled(Box)`
  position: sticky;
  left: 0;
  top: ${({ theme }) =>
    `calc(${theme.ui.spacings.xxl} + 2 * ${theme.ui.spacings.md})`};
  z-index: 1;
  background-color: ${({ theme }) => theme.ui.colors.background};
`

const FilterContent = styled(Box, {
  shouldForwardProp: (prop) => !['columnCount'].includes(prop),
})<{ columnCount: number }>`
  display: grid;
  grid-template-columns: repeat(${({ columnCount }) => columnCount}, 1fr);
  gap: ${({ theme }) => theme.ui.spacings.md};
`

interface FilterButtonProps
  extends Omit<ComponentProps<typeof Button>, 'ref' | 'title'> {
  active?: boolean
  title: ReactNode
  defaultExpanded?: boolean
  /* specify where the filter content should be rendered */
  targetRef?: RefObject<HTMLDivElement | null>
  contentProps?: ComponentProps<typeof FlexBox>
  columnCount?: number
}

export const Filter = memo(
  ({
    active,
    title,
    'aria-label': ariaLabel = 'filter-button',
    children,
    defaultExpanded = false,
    targetRef,
    contentProps,
    columnCount = 3,
    ...rest
  }: FilterButtonProps) => {
    const [isExpanded, setExpanded] = useState(defaultExpanded)

    const toggleExpanded = useHandler(() => setExpanded((prev) => !prev))

    const content = useMemo(
      () => (
        <>
          {/* Cannot use loaders from ui.loader because of circular dependencies */}
          {isExpanded ? (
            <FlexBox
              {...{
                fd: 'column',
                gap: 'md',
                padding: 'md',
                bd: '1px solid',
                bc: 'navy.25',
                bra: 'sm',
                bgc: 'white.100',
                ...contentProps,
              }}>
              <FilterContent
                {...{ 'aria-label': 'filter-content', columnCount }}>
                {children}
              </FilterContent>
            </FlexBox>
          ) : null}
        </>
      ),
      [children, columnCount, contentProps, isExpanded],
    )

    return (
      <>
        <Button
          {...{
            variant: active ? 'primary' : 'secondary',
            icon: FilterIcon,
            'aria-label': ariaLabel,
            onClick: toggleExpanded,
            ...rest,
          }}>
          {title}
          <DropdownIcon {...{ isExpanded, w: 'auto', h: 'auto' }} />
        </Button>
        {targetRef && targetRef.current
          ? createPortal(content, targetRef.current)
          : content}
      </>
    )
  },
)
